import { utils, components } from '@digital-commerce/shared-utils'
import { Spacer } from '@telus-uds/components-web'

import { bool, object } from 'prop-types'
import { useContext } from 'react'
import { ResponsiveCard } from '../StyledContainers'

import localeContext from '../../../contexts/Locale'
import useTranslate from '../../../hooks/useTranslate'

import { buildContractTermLabel } from '../../../utils/display'
import {
  getAALOrRenewalBannerMessage,
  getChangeUrlFromMobilityOffer,
  getMobilityGifts,
  getMobilityPlan,
  getMonthlyTotalPriceOfTerm,
  getTerm
} from '../../../utils/mobility'
import CartItem, { BasicInfo, ActionBar, OfferDetailsContainer } from '../../molecules/CartItem'
import FinancedAccessoryGroup from '../../molecules/FinancedAccessory/FinancedAccessoryGroup'
import MobilityGifts from '../../molecules/MobilityGifts'
import ItemOutOfStock from '../../molecules/RealTimeInventory/ItemOutOfStock'
import OfferDetails from './OfferDetails'
import {
  calculateDueTodaySubTotal,
  getAttributes,
  getPhoneItem,
  isWirelessHomePhone
} from './utils'

const { getDisplayName, isBusinessCart } = utils.cart
const { WithLegal } = components

const MobilityConnectedDevice = ({
  mobilityPhoneOffer,
  cart,
  isRemoveButtonHidden,
  isMobilityWirelessHomePhone,
  isOrbit,
  financedAccessoryGroup
}) => {
  const translate = useTranslate()
  const { lang } = useContext(localeContext)

  const plan = getMobilityPlan(mobilityPhoneOffer)
  const term = getTerm(plan)

  const changeUrl = getChangeUrlFromMobilityOffer(mobilityPhoneOffer)
  const contractTermLabel = buildContractTermLabel(translate, mobilityPhoneOffer)
  const phone = getPhoneItem(mobilityPhoneOffer)
  const mobilityGifts = getMobilityGifts(mobilityPhoneOffer)
  const bannerData = getAALOrRenewalBannerMessage(cart)
  const attributes = getAttributes(
    mobilityPhoneOffer,
    phone,
    plan,
    isMobilityWirelessHomePhone,
    translate,
    lang
  )
  const calculatedDueTodaySubTotal = calculateDueTodaySubTotal(mobilityPhoneOffer)
  const { subTotal: monthlySubTotal } = getMonthlyTotalPriceOfTerm(term, mobilityPhoneOffer) || {}
  const subHeading = isMobilityWirelessHomePhone
    ? translate('subHeading.wirelessHomePhone')
    : translate('subHeading.phonesAndPlans')

  const termsAndConditionsForOrbit = isOrbit && (
    <WithLegal content={translate('orbit.termsAndConditions')} copy={lang} />
  )

  const isWirelessHomePhoneDevice = isWirelessHomePhone(phone)

  const outOfStockNotification = (
    <ItemOutOfStock
      changeUrl={changeUrl}
      device={phone}
      category={isWirelessHomePhoneDevice ? 'wirelessHomePhone' : 'smartphones'}
      noCta={isWirelessHomePhoneDevice}
      isBusinessCart={isBusinessCart(cart)}
    />
  )

  return (
    <>
      <ResponsiveCard variant={{ border: 'line', padding: 'custom' }}>
        <CartItem cartItem={mobilityPhoneOffer}>
          <BasicInfo
            subHeading={subHeading}
            dueTodaySubTotal={calculatedDueTodaySubTotal}
            monthlySubTotal={monthlySubTotal}
            changeUrl={changeUrl}
            term={contractTermLabel}
            itemImageOverride={phone}
            itemDisplayNameOverride={getDisplayName(phone)}
            attributesOverride={attributes}
            mobilityBannerData={bannerData}
            termsAndConditions={termsAndConditionsForOrbit}
            inventory={phone?.inventory}
            mobOutOfStockNotification={outOfStockNotification}
          />
          <ActionBar
            isShowDetailsCtaHidden={false}
            isRemoveButtonHidden={isRemoveButtonHidden}
            hasFinancedAccessoryGroup={Boolean(financedAccessoryGroup)}
          />
          <OfferDetailsContainer>
            <OfferDetails
              mobilityPhoneOffer={mobilityPhoneOffer}
              phone={phone}
              plan={plan}
              cart={cart}
              isMobilityWirelessHomePhone={isMobilityWirelessHomePhone}
              isOrbit={isOrbit}
            />
          </OfferDetailsContainer>
        </CartItem>
        <MobilityGifts gifts={mobilityGifts} />
        <FinancedAccessoryGroup
          accessoryGroup={financedAccessoryGroup}
          isPurchasedWithPhone={true}
          cart={cart}
        />
      </ResponsiveCard>
      <Spacer space={5} />
    </>
  )
}

export default MobilityConnectedDevice

MobilityConnectedDevice.propTypes = {
  mobilityPhoneOffer: object.isRequired,
  cart: object.isRequired,
  isRemoveButtonHidden: bool,
  isMobilityWirelessHomePhone: bool,
  isOrbit: bool,
  financedAccessoryGroup: object
}

MobilityConnectedDevice.defaultProps = {
  isRemoveButtonHidden: false,
  isMobilityWirelessHomePhone: false,
  isOrbit: false,
  financedAccessoryGroup: null
}
